import { useEffect, useState } from "react";
import { Paper, Typography, Box, CircularProgress, Alert } from "@mui/material";
import { School } from "@mui/icons-material";
import { getData } from "../../Services/getData";
import { endpoints } from "../../Utils/UrlEnum";
import { isFreeSubscription } from "../../Utils/Utils";
import genericStyles from "../../Styles/genericStyles.module.css";
import styles from "../../Styles/clientAccount.module.css";
import { updateData } from "../../Services/updateData";
import { Vocabulary } from "../../Utils/Vocabulary";
import {
  ExamData,
  ExamFormData,
  DocumentData,
  WorkpointData,
  Subscription,
} from "../../Utils/Types";
import DocumentsSection from "./DocumentsSection";
import WorkpointSection from "./WorkpointSection";
import ExamsSection from "./ExamsSection";
import EditExamDialog from "./EditExamDialog";
import PurchasePromoBanner from "./PurchasePromoBanner";
import PaymentStatusModal from "./PaymentStatusModal";

export default function ClientAccount() {
  const [exams, setExams] = useState<ExamData[]>([]);
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [workpoint, setWorkpoint] = useState<WorkpointData | null>(null);
  const [subscription, setSubscription] = useState<
    (Subscription & { _id?: string; expiredDate?: string }) | null
  >(null);
  const [activeOrders, setActiveOrders] = useState<any[]>([]);
  const [allDocumentTypes, setAllDocumentTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingExam, setEditingExam] = useState<ExamData | null>(null);
  const [editFormData, setEditFormData] = useState<ExamFormData>({
    examDate: "",
  });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [clientData, setClientData] = useState<any>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const clientId = localStorage.getItem("clientId");

  const fetchExams = async (currentPage = 0, reset = false) => {
    try {
      setLoading(true);
      const response = await getData(
        `${endpoints.exams}/forClient/${clientId}/paginated?page=${currentPage}&perPage=10`
      );

      if (response.data) {
        if (reset) {
          setExams(response.data.results || []);
        } else {
          setExams((prev) => [...prev, ...(response.data.results || [])]);
        }
        setHasMore(response.data.hasMore || false);
      }
    } catch (err: any) {
      setError(
        "Nu s-au putut încărca examenele. Vă rugăm să încercați din nou."
      );
      console.error("Error fetching exams:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const url = endpoints.client.portalDocuments.replace(":clientId", clientId || "");
      const response = await getData(url);
      if (response.data) {
        setDocuments(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
    }
  };

  const fetchAllDocumentTypes = async () => {
    try {
      const response = await getData(endpoints.documentTypes);
      if (response.data) {
        setAllDocumentTypes(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching document types:", err);
    }
  };

  const getClientData = async () => {
    const url = `${endpoints.client.main}/${clientId}`;
    await getData(url).then((response) => {
      if (response.data) {
        setClientData(response.data);
        setWorkpoint(response.data?.workPointId || null);
        setSubscription({
          ...response.data?.subscriptionId,
          subscriptionExpiresAt: response.data?.subscriptionExpiresAt,
        });
        // Fetch pending orders if no subscription
        if (!response.data?.subscriptionId?._id) {
          fetchActiveOrders();
        }
      }
    });
  };

  const fetchActiveOrders = async () => {
    try {
      const response = await getData(
        `${endpoints.clientActiveOrders.replace(":clientId", clientId || "")}`
      );
      if (response.data) {
        setActiveOrders(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching pending orders:", err);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchExams(0, true);
      getClientData();
      fetchDocuments();
      fetchAllDocumentTypes();
    }
  }, [clientId]);

  /**
   * Check if returning from payment page (paymentComplete=true in query params)
   * This happens after user completes payment and is redirected back to client-account
   */
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("paymentComplete") === "true") {
      setShowPaymentModal(true);
      // Remove the query parameter from the URL without reloading
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  /**
   * Handles saving an edited exam by updating the exam data and then updating the exams state with the new data.
   * If the exam does not exist, does nothing.
   * If an error occurs while updating the exam, sets the error state.
   * If the updating is successful, closes the edit form and updates the exams state with the new data.
   */
  const handleSaveExam = async () => {
    if (!editingExam) return;

    try {
      setSaving(true);
      const updatedData = {
        examDate: editFormData.examDate,
      };

      await updateData(`${endpoints.exams}/${editingExam._id}`, updatedData);

      setExams((prev) =>
        prev.map((exam) =>
          exam._id === editingExam._id ? { ...exam, ...updatedData } : exam
        )
      );

      handleCloseEdit();
    } catch (err: any) {
      setError(
        "Nu s-a putut actualiza examenul. Vă rugăm să încercați din nou."
      );
      console.error("Error updating exam:", err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Closes the edit exam form by setting the editingExam state to null and resetting the edit form data.
   */
  const handleCloseEdit = () => {
    setEditingExam(null);
    setEditFormData({
      examDate: "",
    });
  };

  /**
   * Updates the edit form data with a new value for the specified field.
   * @param {keyof ExamFormData} field - The field to update in the edit form data.
   * @param {string} value - The new value for the specified field.
   */
  const handleFormDataChange = (field: keyof ExamFormData, value: string) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Loads the next page of exams if there are more exams to load and the exams are not currently loading.
   */
  const loadMore = () => {
    if (hasMore && !loading) {
      setPage((prev) => prev + 1);
      fetchExams(page + 1, false);
    }
  };

  if (loading && exams.length === 0) {
    return (
      <div className={genericStyles.mainContainer}>
        <Paper elevation={0} className={genericStyles.scrollableContainer}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        </Paper>
      </div>
    );
  }

  return (
    <div className={genericStyles.mainContainer}>
      <div
        className={genericStyles.scrollableContainer}
        style={{ height: "86vh" }}
      >
        <Box className={styles.header}>
          <Typography
            variant="h4"
            className={`${styles.title} ${genericStyles.titleColor}`}
          >
            <School className={styles.titleIcon} />
            {Vocabulary.clientAccount}
          </Typography>
          <Typography variant="body1" className={styles.subtitle}>
            {Vocabulary.manageExams}
          </Typography>
        </Box>

        <PurchasePromoBanner
          subscription={subscription}
          activeOrders={activeOrders}
          clientData={clientData}
          onOrderAdded={fetchActiveOrders}
        />

        {error && (
          <Alert severity="error" style={{ marginBottom: "16px" }}>
            {error}
          </Alert>
        )}

        {!isFreeSubscription() && (
          <>
            {activeOrders?.length && (
              <ExamsSection
                exams={exams}
                loading={loading}
                hasMore={hasMore}
                onLoadMore={loadMore}
                onExamsRefresh={() => fetchExams(0, true)}
                activeOrders={activeOrders}
              />
            )}

            <DocumentsSection
              documents={documents}
              activeOrders={activeOrders}
              allDocumentTypes={allDocumentTypes}
            />

            <WorkpointSection workpoint={workpoint} />
          </>
        )}

        <EditExamDialog
          open={Boolean(editingExam)}
          exam={editingExam}
          formData={editFormData}
          saving={saving}
          onClose={handleCloseEdit}
          onSave={handleSaveExam}
          onFormDataChange={handleFormDataChange}
        />

        <PaymentStatusModal
          open={showPaymentModal}
          onClose={() => {
            getClientData();
            setShowPaymentModal(false);
          }}
        />
      </div>
    </div>
  );
}
